import { motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useMemo } from 'react';
import ProgressDashes from '../../components/ProgressDashes';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { StoryNavigation } from '../../components/StoryNavigation';
import { useStoryNavigation } from '../../hooks/useStoryNavigation';
import ShareButton from '../../components/ShareButton';

const IncomeGapPage = () => {
  const navigate = useNavigate();
  const transactions = useSelector((state: RootState) => state.transactions.items);
  const { goToNext, goToPrev, hasNext, hasPrev } = useStoryNavigation();

  useEffect(() => {
    const timer = setTimeout(() => {
      navigate('/insights/money-wrapped');
    }, 10000);

    return () => clearTimeout(timer);
  }, [navigate]);

  const formatPercentage = (decimal: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'percent',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(decimal);
  };

  return (
    <div className="min-h-screen bg-[#221A51]/[0.07] relative flex flex-col">
      <StoryNavigation
        onNext={goToNext}
        onPrev={goToPrev}
        hasNext={hasNext}
        hasPrev={hasPrev}
      />
      <div className="flex-1 px-4 sm:px-16 pt-20 pb-8 max-w-4xl mx-auto w-full flex flex-col">
        <ProgressDashes currentStep={10} totalSteps={11} />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-12 flex flex-col flex-1"
        >
          <h1 className="text-3xl sm:text-4xl md:text-[34px] font-black text-[#221A51] leading-[140%] mb-8">
            Income vs
            <br />
            Cost of Living
          </h1>

          <div className="bg-white rounded-xl p-6 sm:p-8 border-2 border-[#221A51] shadow-[4px_4px_0px_0px_#221A51] flex-1 flex flex-col">
            <div className="flex-1 flex flex-col justify-center items-center gap-16">
              {/* Monthly Comparison */}
              <div className="text-center">
                <div className="text-[#221A51]/60 text-lg mb-8">Last month</div>
                <div className="flex justify-center items-center space-x-16">
                  <div>
                    <div className="text-[#221A51] font-bold text-xl mb-4">Your income</div>
                    <div className="text-4xl font-mono">
                      {formatPercentage(0)} →
                    </div>
                  </div>
                  <div>
                    <div className="text-[#221A51] font-bold text-xl mb-4">Cost of living</div>
                    <div className="text-4xl font-mono text-[#FF9B9B]">
                      +{formatPercentage(0.05)} ↗️
                    </div>
                  </div>
                </div>
              </div>

              {/* Impact Message */}
              <div className="bg-[#1A1425] rounded-lg p-8 sm:p-10 w-full max-w-2xl">
                <div className="text-center">
                  <p className="text-white/90 text-xl sm:text-2xl font-medium leading-[1.6]">
                    While your income stayed the same,
                    <br />
                    everything got <span className="text-[#FF9B9B] font-semibold">5% more expensive</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex justify-center mt-8"
        >
          <ShareButton
            title="Income vs Cost of Living 📊"
            text={`While my income stayed the same, everything got 5% more expensive this month! 😅`}
          />
        </motion.div>
      </div>
    </div>
  );
};

export default IncomeGapPage;
